import {
  closestCorners,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CrudViewContext, ListQuery, Paginated } from '@restavo/crud';
import IssueDetails from 'src/components/Issue/IssueDetails';
import IssueLane from 'src/components/Issue/IssueLane';
import { useIssueBoard } from 'src/hooks/useIssueBoard';
import { BranchScope } from 'src/services/apis/tenantRequests';
import { IssueEntity } from 'src/services/apis/types';
import { getIssueStatusText, IssueStatusEnum } from 'src/utils/issue';
import { BOARD_LANES } from 'src/utils/issueBoard';

interface IssueBoardProps {
  context: CrudViewContext<IssueEntity, unknown, unknown>;
  list: (scope: BranchScope, query?: ListQuery) => Promise<Paginated<IssueEntity>>;
}

/** The card's title, for the drag announcements. */
const titleOf = (id: string, lanes: Record<number, { rows: IssueEntity[] }>): string =>
  BOARD_LANES.flatMap((status) => lanes[status].rows).find((row) => row.id === id)?.title ?? '';

/** `lane-3` → 3. A drop lands either on a card or on the lane's own droppable. */
const laneOf = (id: string, lanes: Record<number, { rows: IssueEntity[] }>): IssueStatusEnum | null => {
  if (id.startsWith('lane-')) {
    return Number(id.slice('lane-'.length)) as IssueStatusEnum;
  }
  const found = BOARD_LANES.find((status) => lanes[status].rows.some((row) => row.id === id));
  return found ?? null;
};

const IssueBoard = React.memo(({ context, list }: IssueBoardProps) => {
  const { t } = useTranslation();
  const board = useIssueBoard({ context, list });
  const [viewing, setViewing] = useState<IssueEntity | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Space lifts, arrows move, space drops. Without this the board is pointer-only and the
    // table view becomes load-bearing for keyboard users forever.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // dnd-kit's defaults are English string literals. A screen-reader user on the Vietnamese
  // portal would hear the drag narrated in English without this.
  const announcements = {
    onDragStart: ({ active }: { active: { id: string | number } }) =>
      t('issueBoard.announceLifted', { title: titleOf(String(active.id), board.lanes) }),
    onDragOver: ({ over }: { over: { id: string | number } | null }) =>
      over
        ? t('issueBoard.announceOver', { lane: t(getIssueStatusText(laneOf(String(over.id), board.lanes) ?? 0)) })
        : '',
    onDragEnd: ({ over }: { over: { id: string | number } | null }) =>
      over ? t('issueBoard.announceDropped') : t('issueBoard.announceCancelled'),
    onDragCancel: () => t('issueBoard.announceCancelled'),
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      return;
    }
    const from = laneOf(String(active.id), board.lanes);
    const to = laneOf(String(over.id), board.lanes);
    if (from === null || to === null) {
      return;
    }

    const destination = board.lanes[to].rows.filter((row) => row.id !== active.id);
    const overIndex = destination.findIndex((row) => row.id === over.id);
    const insertIndex = overIndex === -1 ? destination.length : overIndex;

    if (from === to && board.lanes[to].rows.findIndex((row) => row.id === active.id) === insertIndex) {
      return;
    }

    board.move({ id: String(active.id), from, to, insertIndex });
  };

  return (
    <div className={'issue-board'}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
        accessibility={{ announcements }}
      >
        <div className={'issue-board__lanes'}>
          {BOARD_LANES.map((status) => (
            <IssueLane
              key={status}
              status={status}
              rows={board.lanes[status].rows}
              total={board.lanes[status].total}
              isLoading={board.lanes[status].isLoading}
              canWrite={context.canWrite}
              onView={setViewing}
              onEdit={context.onEdit}
              onDelete={context.onDelete}
            />
          ))}
        </div>
      </DndContext>
      <IssueDetails issue={viewing} onClose={() => setViewing(null)} />
    </div>
  );
});

IssueBoard.displayName = 'IssueBoard';

export default IssueBoard;
